import React, { useState, useMemo } from 'react';
import {
  TrendingUp,
  DollarSign,
  Download,
  Calendar,
  PieChart as PieIcon,
  BarChart3,
  CheckCircle2,
  AlertCircle,
  FileSpreadsheet,
  FileText,
  Wrench,
  Layers,
  PackageCheck,
  CreditCard,
  Clock,
  ArrowDownRight,
  ArrowUpRight,
  Filter,
  Check,
  ChevronRight,
  CalendarDays,
  Coins,
  BadgeAlert,
  Banknote,
  Landmark,
  Wallet,
  ArrowLeftRight,
  UserCheck,
  User,
  Users,
  UserPlus,
  Building2,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { AppUser, CompanyInfo, ServiceTicket, PaymentMethod, InventoryItem, StockMovement } from '../types';
import {
  calculateTicketTotal,
  calculateTicketPartsCost,
  formatTL,
  STATUS_LABELS,
  STATUS_COLORS,
  SERVICE_TYPE_LABELS,
  SERVICE_TYPE_COLORS,
  getTicketReportDate,
  normalizePaymentMethod,
  calculatePaymentBreakdown,
  PaymentMethodBreakdown,
  getStoredUsers,
  calculateContractCompanyMetrics,
  isTicketAssignedToUser,
  getTicketAssignedTechnicianDisplay,
  getTicketPaymentSplit,
  getTicketPaymentSplitForPeriod,
  formatTicketPaymentMethodDisplay,
  formatDateTR,
  getTicketContractBreakdown,
} from '../utils/storage';
import {
  exportMonthlyReportPDF,
  exportMonthlyCollectionsReportPDF,
  exportDailyReportPDF,
  exportStockUsageReportPDF,
  exportTechnicianReportPDF,
  exportDeviceAreaMonthlyReportPDF,
} from '../utils/pdfGenerator';
import {
  exportMonthlyReportExcel,
  exportDailyReportExcel,
  exportStockUsageReportExcel,
  exportTechnicianReportExcel,
} from '../utils/excelGenerator';
import { StockUsageReportSection } from './StockUsageReportSection';
import { TechnicianReportSection } from './TechnicianReportSection';
import { ContractedCompaniesView } from './ContractedCompaniesView';
import { PrinterMonthlyReportSection } from './PrinterMonthlyReportSection';

interface ReportsViewProps {
  tickets: ServiceTicket[];
  companyInfo: CompanyInfo;
  inventory?: InventoryItem[];
  stockMovements?: StockMovement[];
  currentUser?: AppUser | null;
  onSaveCompanyInfo?: (company: CompanyInfo) => void;
}

const MONTH_NAMES_TR: Record<string, string> = {
  '01': 'Ocak',
  '02': 'Şubat',
  '03': 'Mart',
  '04': 'Nisan',
  '05': 'Mayıs',
  '06': 'Haziran',
  '07': 'Temmuz',
  '08': 'Ağustos',
  '09': 'Eylül',
  '10': 'Ekim',
  '11': 'Kasım',
  '12': 'Aralık',
};

const CHART_COLORS = ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#64748b'];
const PAYMENT_METHOD_COLORS = {
  'Nakit': '#10b981',
  'Kredi Kartı': '#6366f1',
  'Havale / EFT': '#06b6d4',
};

const CollectionsTransactionsSection: React.FC<{
  tickets: ServiceTicket[];
  periodKey: string;
  periodName: string;
  onExportPDF: () => void;
}> = ({ tickets, periodKey, periodName, onExportPDF }) => {
  const rows = useMemo(() => tickets.flatMap(ticket => {
    if (ticket.isDeleted || ticket.serviceType === 'Sozlesmeli' || ticket.contractedCompanyId) return [];
    const explicit = Array.isArray(ticket.payments) ? ticket.payments : [];
    if (explicit.length) {
      return explicit
        .filter(payment => payment.date?.startsWith(periodKey) && Number(payment.amount) > 0)
        .map(payment => ({
          date: payment.date,
          ticketId: ticket.id,
          customer: ticket.customerName || '-',
          device: `${ticket.deviceType || ''} ${ticket.deviceBrand || ''} ${ticket.deviceModel || ''}`.trim() || '-',
          method: normalizePaymentMethod(payment.method),
          amount: Number(payment.amount) || 0,
        }));
    }
    if (!ticket.paymentDate?.startsWith(periodKey)) return [];
    const split = getTicketPaymentSplitForPeriod(ticket, periodKey);
    return [
      { method: 'Nakit' as const, amount: split.cash },
      { method: 'Kredi Karti' as const, amount: split.card },
      { method: 'EFT/Havale' as const, amount: split.transfer },
    ].filter(part => part.amount > 0).map(part => ({
      date: ticket.paymentDate!, ticketId: ticket.id, customer: ticket.customerName || '-',
      device: `${ticket.deviceType || ''} ${ticket.deviceBrand || ''} ${ticket.deviceModel || ''}`.trim() || '-',
      method: part.method, amount: part.amount,
    }));
  }).sort((a, b) => a.date.localeCompare(b.date) || a.ticketId.localeCompare(b.ticketId)), [tickets, periodKey]);
  const total = rows.reduce((sum, row) => sum + row.amount, 0);
  const methodTotals = rows.reduce((acc, row) => {
    acc[row.method] = (acc[row.method] || 0) + row.amount;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="p-5 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-bold text-slate-900">{periodName} Tahsilat Hareketleri</h3>
          <p className="text-xs text-slate-500 mt-1">Seçilen dönemde alınan ödemeler, ödeme tarihi ve servis formuna göre gösterilir.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xl font-black font-mono text-emerald-700">{formatTL(total)}</div>
          <button type="button" onClick={onExportPDF} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900 text-white text-xs font-bold">
            <Download className="w-4 h-4" /> PDF Al
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 border-b border-slate-100">
        {[["Nakit", "Nakit"], ["Kredi Kartı", "Kredi Karti"], ["Havale / EFT", "EFT/Havale"]].map(([label, key]) => (
          <div key={key} className="rounded-xl bg-slate-50 border border-slate-200 p-3">
            <div className="text-xs text-slate-500">{label}</div>
            <div className="font-bold font-mono text-slate-900 mt-1">{formatTL(methodTotals[key] || 0)}</div>
          </div>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs min-w-[700px]">
          <thead className="bg-slate-50 text-slate-600"><tr>
            {['Ödeme Tarihi', 'Servis No', 'Müşteri', 'Cihaz', 'Ödeme Türü', 'Tutar'].map(label => <th key={label} className="text-left px-4 py-3 font-bold">{label}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row, index) => <tr key={`${row.ticketId}-${row.date}-${index}`}>
              <td className="px-4 py-3">{formatDateTR(row.date)}</td><td className="px-4 py-3 font-bold">{row.ticketId}</td>
              <td className="px-4 py-3">{row.customer}</td><td className="px-4 py-3">{row.device}</td>
              <td className="px-4 py-3">{row.method}</td><td className="px-4 py-3 text-right font-bold font-mono">{formatTL(row.amount)}</td>
            </tr>)}
            {!rows.length && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">Bu dönem için tahsilat kaydı bulunmuyor.</td></tr>}
          </tbody>
          {rows.length > 0 && <tfoot className="bg-slate-50"><tr><td colSpan={5} className="px-4 py-3 font-bold text-right">Toplam Tahsilat</td><td className="px-4 py-3 text-right font-black font-mono text-emerald-700">{formatTL(total)}</td></tr></tfoot>}
        </table>
      </div>
    </div>
  );
};

export type ReportMode = 'daily' | 'monthly' | 'collections' | 'printerMonthly' | 'technician' | 'stock' | 'contracted';
export type TableFilter = 'all' | 'delivered' | 'paid' | 'pending' | 'cash' | 'card' | 'transfer';
export type DistributionChartType = 'payment' | 'device';

export const ReportsView: React.FC<ReportsViewProps> = ({
  tickets,
  companyInfo,
  inventory = [],
  stockMovements = [],
  currentUser,
  onSaveCompanyInfo,
}) => {
  const isTechnician = currentUser?.role === 'technician';
  const isLimitedTechnician = currentUser?.role === 'limited_technician';
  const [reportMode, setReportMode] = useState<ReportMode>(
    isLimitedTechnician ? 'daily' : isTechnician ? 'technician' : 'monthly'
  );
  const [tableFilter, setTableFilter] = useState<TableFilter>('all');
  const [chartType, setChartType] = useState<DistributionChartType>('payment');

  // Today ISO Date string YYYY-MM-DD
  const todayKey = useMemo(() => {
    const now = new Date();
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
    return local.toISOString().substring(0, 10);
  }, []);

  // Yesterday ISO Date string
  const yesterdayKey = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
    return local.toISOString().substring(0, 10);
  }, []);

  // Available unique days from ticket activities
  const availableDays = useMemo(() => {
    const set = new Set<string>();
    tickets.forEach(t => {
      const rDate = getTicketReportDate(t);
      if (rDate) set.add(rDate.substring(0, 10));
      if (t.createdAt) set.add(t.createdAt.substring(0, 10));
      if (t.deliveryDate) set.add(t.deliveryDate.substring(0, 10));
      if (t.paymentDate) set.add(t.paymentDate.substring(0, 10));
      if (t.payments && Array.isArray(t.payments)) {
        t.payments.forEach(p => {
          if (p.date) set.add(p.date.substring(0, 10));
        });
      }
    });
    set.add(todayKey);
    return Array.from(set).sort().reverse();
  }, [tickets, todayKey]);

  // Selected Day state (Default today or latest day with activity)
  const [selectedDayKey, setSelectedDayKey] = useState<string>(
    availableDays[0] || todayKey
  );

  // Available unique months (YYYY-MM)
  const availableMonths = useMemo(() => {
    const set = new Set<string>();
    tickets.forEach(t => {
      const rDate = getTicketReportDate(t);
      if (rDate) {
        set.add(rDate.substring(0, 7));
      }
      if (t.createdAt) set.add(t.createdAt.substring(0, 7));
      if (t.deliveryDate) set.add(t.deliveryDate.substring(0, 7));
      if (t.paymentDate) set.add(t.paymentDate.substring(0, 7));
      if (t.payments && Array.isArray(t.payments)) {
        t.payments.forEach(p => {
          if (p.date) set.add(p.date.substring(0, 7));
        });
      }
    });
    set.add(todayKey.substring(0, 7));
    return Array.from(set).sort().reverse();
  }, [tickets, todayKey]);

  const [selectedMonthKey, setSelectedMonthKey] = useState<string>(
    availableMonths[0] || todayKey.substring(0, 7)
  );

  // Stock deduction report selected period ('all' or YYYY-MM)
  const [selectedStockPeriod, setSelectedStockPeriod] = useState<string>(
    availableMonths[0] || todayKey.substring(0, 7)
  );

  // Formatted display names
  const formattedDayName = useMemo(() => {
    try {
      const [y, m, d] = selectedDayKey.split('-');
      const date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
      return new Intl.DateTimeFormat('tr-TR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        weekday: 'long',
      }).format(date);
    } catch {
      return selectedDayKey;
    }
  }, [selectedDayKey]);

  const formattedMonthName = useMemo(() => {
    const [year, month] = selectedMonthKey.split('-');
    return `${MONTH_NAMES_TR[month] || month} ${year}`;
  }, [selectedMonthKey]);

  const formattedStockPeriodName = useMemo(() => {
    if (selectedStockPeriod === 'all') return 'Tüm Zamanlar';
    try {
      const [year, month] = selectedStockPeriod.split('-');
      return `${MONTH_NAMES_TR[month] || month} ${year}`;
    } catch {
      return selectedStockPeriod;
    }
  }, [selectedStockPeriod]);

  // Tickets filtered by active report mode (Daily vs Monthly)
  const currentReportTickets = useMemo(() => {
    if (reportMode === 'daily') {
      return tickets.filter(t => {
        if (t.isDeleted) return false;
        // Günlük finans/kazanç raporunda sözleşmeli servisler yer almaz.
        // Sözleşmeli kayıtlar yalnızca "Sözleşmeli Rapor" bölümünden takip edilir.
        if (t.serviceType === 'Sozlesmeli' || Boolean(t.contractedCompanyId)) {
          return false;
        }
      return Boolean(getTicketReportDate(t) && getTicketReportDate(t).startsWith(selectedDayKey));
      });
    } else {
      return tickets.filter(t => {
        if (t.isDeleted) return false;
        // Aylık kazanç raporunda sözleşmeli servisler kesinlikle yer almaz.
        // Bu kayıtlar yalnızca "Sözleşmeli Rapor" bölümünden takip edilir.
        if (reportMode === 'monthly' && (t.serviceType === 'Sozlesmeli' || Boolean(t.contractedCompanyId))) {
          return false;
        }
        return Boolean(getTicketReportDate(t) && getTicketReportDate(t).startsWith(selectedMonthKey));
      });
    }
  }, [tickets, reportMode, selectedDayKey, selectedMonthKey]);

  // Financial & Operational Metrics calculation for the selected period
  const metrics = useMemo(() => {
    // Sözleşme tahsilatları günlük/aylık kazanç-kasa raporlarına dahil edilmez.
    // Bunlar yalnızca Sözleşmeli Rapor içinde izlenir.
    const periodContractPayments = 0;
    const selectedPaymentPeriod = reportMode === 'daily' ? selectedDayKey : selectedMonthKey;

    let totalRevenue = 0;
    let totalVat = 0;
    let totalExVatRevenue = 0;
    let totalCollected = 0;
    let totalRemaining = 0;
    let totalPartsCost = 0;
    let totalSubServiceCost = 0;
    let totalLaborRevenue = 0;
    let deliveredCount = 0;
    let deliveredRevenue = 0;
    let pendingPaymentCount = 0;
    let fullyPaidCount = 0;

    // Track contracted tickets separately
    let contractedTicketsCount = 0;
    let contractedServiceConsumed = 0;
    let contractedPartsCost = 0;
    let extraOutOfContractRevenue = 0;
    let extraOutOfContractCollected = 0;
    let extraOutOfContractProfit = 0;

    currentReportTickets.forEach(t => {
      const isContracted = t.serviceType === 'Sozlesmeli' || Boolean(t.contractedCompanyId);
      const partsCost = calculateTicketPartsCost(t.parts);
      const subCost = t.subServiceCost || 0;

      totalPartsCost += partsCost;
      totalSubServiceCost += subCost;

      if (isContracted) {
        // Contracted ticket:
        // In-contract portion is covered by the company contract balance.
        // If extra out-of-contract parts exist, they count as EXTERNAL COLLECTION (harici tahsilat) & extra profit outside contract balance.
        const breakdown = getTicketContractBreakdown(t);
        contractedTicketsCount++;
        contractedServiceConsumed += breakdown.inContractGrandTotal;
        contractedPartsCost += calculateTicketPartsCost(breakdown.inContractParts);

        if (breakdown.hasOutOfContractParts) {
          const extraRev = breakdown.outOfContractTotal;
          const extraCost = breakdown.outOfContractCost;
          const extraProf = extraRev - extraCost;

          let paidExtra = 0;
          if (t.paymentStatus === 'Odendi') {
            paidExtra = t.paidAmount !== undefined && t.paidAmount > 0 ? t.paidAmount : extraRev;
            fullyPaidCount++;
          } else if (t.paymentStatus === 'KismiOdendi') {
            paidExtra = t.paidAmount || 0;
            if (extraRev > paidExtra) pendingPaymentCount++;
          } else {
            paidExtra = 0;
            if (extraRev > 0) pendingPaymentCount++;
          }

          const remExtra = Math.max(0, extraRev - paidExtra);

          totalRevenue += extraRev;
          totalExVatRevenue += extraRev;
          totalCollected += paidExtra;
          totalRemaining += remExtra;
          extraOutOfContractRevenue += extraRev;
          extraOutOfContractCollected += paidExtra;
          extraOutOfContractProfit += extraProf;
        }

        if (t.status === 'TeslimEdildi') {
          deliveredCount++;
        }
      } else {
        // Standard ticket (Ücretli, Garantili, Servis Garantili)
        const { subtotal, vatAmount, grandTotal } = calculateTicketTotal(
          t.parts,
          t.laborCost,
          t.vatRate,
          t.discount,
          t.isVatIncluded, t.roundingAdjustment || 0
        );

        const allTimePaid = getTicketPaymentSplit(t).total;
        if (allTimePaid >= grandTotal - 0.01 && grandTotal > 0) fullyPaidCount++;
        else if (grandTotal > allTimePaid) pendingPaymentCount++;

        const remaining = Math.max(0, grandTotal - allTimePaid);
        // Servis formu ve giderler işlem kaydında tutulur; finansal ciro ödeme tarihinde raporlanır.
        totalRevenue += grandTotal;
        totalVat += vatAmount;
        totalExVatRevenue += subtotal;
        totalRemaining += remaining;
        totalLaborRevenue += t.laborCost;

        if (t.status === 'TeslimEdildi') {
          deliveredCount++;
          deliveredRevenue += grandTotal;
        }
      }
    });

    // Gelir, formun işlem tarihine değil paranın alındığı tarihe yazılır.
    totalRevenue = 0;
    totalVat = 0;
    totalExVatRevenue = 0;
    totalLaborRevenue = 0;
    totalCollected = 0;
    deliveredRevenue = 0;
    tickets.forEach(ticket => {
      if (ticket.isDeleted || ticket.serviceType === 'Sozlesmeli' || Boolean(ticket.contractedCompanyId)) return;
      const received = getTicketPaymentSplitForPeriod(ticket, selectedPaymentPeriod).total;
      if (received <= 0) return;
      const { subtotal, vatAmount, grandTotal } = calculateTicketTotal(
        ticket.parts, ticket.laborCost, ticket.vatRate, ticket.discount, ticket.isVatIncluded, ticket.roundingAdjustment || 0
      );
      const paidRatio = grandTotal > 0 ? received / grandTotal : 0;
      totalRevenue += received;
      totalVat += vatAmount * paidRatio;
      totalExVatRevenue += subtotal * paidRatio;
      totalLaborRevenue += (ticket.laborCost || 0) * paidRatio;
      totalCollected += received;
      if (ticket.status === 'TeslimEdildi') deliveredRevenue += received;
    });

    const totalCost = totalPartsCost + totalSubServiceCost;
    const grossProfit = totalRevenue + periodContractPayments - totalCost;
    const netProfitExVat = totalExVatRevenue + periodContractPayments - totalCost;
    const collectionRate =
      currentReportTickets.length > 0
        ? Math.min(
            100,
            Math.round(
              (totalCollected / Math.max(1, currentReportTickets.reduce((sum, ticket) => sum + calculateTicketTotal(ticket.parts, ticket.laborCost, ticket.vatRate, ticket.discount, ticket.isVatIncluded, ticket.roundingAdjustment || 0).grandTotal, 0))) * 100
            )
          )
        : 0;
    const deliveryRate =
      currentReportTickets.length > 0
        ? Math.round((deliveredCount / currentReportTickets.length) * 100)
        : 0;

    return {
      totalTickets: currentReportTickets.length,
      deliveredCount,
      deliveredRevenue,
      deliveryRate,
      totalRevenue,
      totalVat,
      totalExVatRevenue,
      totalCollected,
      totalRemaining,
      collectionRate,
      totalPartsCost,
      totalSubServiceCost,
      totalCost,
      totalLaborRevenue,
      grossProfit,
      netProfit: netProfitExVat,
      netProfitExVat,
      pendingPaymentCount,
      fullyPaidCount,
      periodContractPayments,
      contractedTicketsCount,
      contractedServiceConsumed,
      contractedPartsCost,
      extraOutOfContractRevenue,
      extraOutOfContractCollected,
      extraOutOfContractProfit,
    };
  }, [currentReportTickets, tickets, reportMode, selectedDayKey, selectedMonthKey]);

  // Payment Method Breakdown
  const paymentBreakdown = useMemo<PaymentMethodBreakdown>(() => {
    const periodKey = reportMode === 'daily' ? selectedDayKey : selectedMonthKey;
    const result: PaymentMethodBreakdown = {
      cash: { amount: 0, count: 0, percentage: 0 },
      card: { amount: 0, count: 0, percentage: 0 },
      transfer: { amount: 0, count: 0, percentage: 0 },
      totalCollected: 0,
    };
    tickets.forEach(t => {
      if (t.isDeleted || t.serviceType === 'Sozlesmeli' || Boolean(t.contractedCompanyId)) return;
      const split = getTicketPaymentSplitForPeriod(t, periodKey);
      if (split.cash > 0) { result.cash.amount += split.cash; result.cash.count++; }
      if (split.card > 0) { result.card.amount += split.card; result.card.count++; }
      if (split.transfer > 0) { result.transfer.amount += split.transfer; result.transfer.count++; }
      result.totalCollected += split.total;
    });
    if (result.totalCollected > 0) {
      result.cash.percentage = (result.cash.amount / result.totalCollected) * 100;
      result.card.percentage = (result.card.amount / result.totalCollected) * 100;
      result.transfer.percentage = (result.transfer.amount / result.totalCollected) * 100;
    }
    return result;
  }, [tickets, reportMode, selectedDayKey, selectedMonthKey]);

  // Data for Payment Method Breakdown (PieChart)
  const paymentMethodPieData = useMemo(() => {
    return [
      { name: 'Nakit', value: paymentBreakdown.cash.amount, count: paymentBreakdown.cash.count, color: '#10b981' },
      { name: 'Kredi Kartı', value: paymentBreakdown.card.amount, count: paymentBreakdown.card.count, color: '#6366f1' },
      { name: 'Havale / EFT', value: paymentBreakdown.transfer.amount, count: paymentBreakdown.transfer.count, color: '#06b6d4' },
    ].filter(item => item.value > 0);
  }, [paymentBreakdown]);

  // Data for Category Breakdown (PieChart)
  const deviceCategoryData = useMemo(() => {
    const map = new Map<string, number>();
    const periodKey = reportMode === 'daily' ? selectedDayKey : selectedMonthKey;
    tickets.forEach(t => {
      if (t.isDeleted || t.serviceType === 'Sozlesmeli' || Boolean(t.contractedCompanyId)) return;
      const grandTotal = getTicketPaymentSplitForPeriod(t, periodKey).total;
      if (grandTotal <= 0) return;
      const cat = t.deviceType || 'Diğer';
      map.set(cat, (map.get(cat) || 0) + grandTotal);
    });

    return Array.from(map.entries()).map(([name, value]) => ({ name, value }));
  }, [tickets, reportMode, selectedDayKey, selectedMonthKey]);

  // Cihaz alanı raporu: Ayarlardan Yazıcı/Tarayıcı veya Bilgisayar olarak atanır.
  const deviceAreaReport = useMemo(() => {
    const result = {
      printer: { count: 0, revenue: 0, collected: 0, remaining: 0 },
      computer: { count: 0, revenue: 0, collected: 0, remaining: 0 },
    };

    const periodKey = reportMode === 'daily' ? selectedDayKey : selectedMonthKey;
    tickets.forEach(t => {
      if (t.isDeleted || t.serviceType === 'Sozlesmeli' || Boolean(t.contractedCompanyId)) return;
      const received = getTicketPaymentSplitForPeriod(t, periodKey).total;
      if (received <= 0) return;
      const type = t.deviceType || '';
      const lower = type.toLocaleLowerCase('tr-TR');
      const inferred: 'printer' | 'computer' =
        lower.includes('yazıcı') || lower.includes('yazici') ||
        lower.includes('tarayıcı') || lower.includes('tarayici') ||
        lower.includes('printer') || lower.includes('scanner') ||
        lower.includes('fotokopi')
          ? 'printer'
          : 'computer';
      const area = companyInfo.deviceAreaAssignments?.[type] || inferred;
      result[area].count += 1;
      result[area].revenue += received;
      result[area].collected += received;
    });
    tickets.forEach(t => {
      if (t.isDeleted || t.serviceType === 'Sozlesmeli' || Boolean(t.contractedCompanyId)) return;
      const type = t.deviceType || '';
      const lower = type.toLocaleLowerCase('tr-TR');
      const inferred: 'printer' | 'computer' = lower.includes('yazıcı') || lower.includes('yazici') || lower.includes('tarayıcı') || lower.includes('tarayici') || lower.includes('printer') || lower.includes('scanner') || lower.includes('fotokopi') ? 'printer' : 'computer';
      const area = companyInfo.deviceAreaAssignments?.[type] || inferred;
      const { grandTotal } = calculateTicketTotal(t.parts, t.laborCost, t.vatRate, t.discount, t.isVatIncluded, t.roundingAdjustment || 0);
      result[area].remaining += Math.max(0, grandTotal - getTicketPaymentSplit(t).total);
    });

    return result;
  }, [tickets, companyInfo.deviceAreaAssignments, reportMode, selectedDayKey, selectedMonthKey]);

  // Data for Cashflow Breakdown (Collected vs Remaining vs Cost vs VAT)
  const financialBreakdownData = useMemo(() => {
    return [
      { name: 'Tahsil Edilen', value: metrics.totalCollected, color: '#10b981' },
      { name: 'Alınacak (Kalan)', value: metrics.totalRemaining, color: '#f59e0b' },
      { name: 'Parça Maliyeti', value: metrics.totalPartsCost, color: '#ef4444' },
      { name: 'KDV Tutarı', value: metrics.totalVat, color: '#8b5cf6' },
    ].filter(item => item.value > 0);
  }, [metrics]);

  // Monthly Comparison Trend (Last 6 Months)
  const sixMonthsTrendData = useMemo(() => {
    return availableMonths.slice(0, 6).reverse().map(mKey => {
      const [year, month] = mKey.split('-');
      const label = `${MONTH_NAMES_TR[month] || month} '${year.substring(2)}`;
      const serviceMonthTickets = tickets.filter(t =>
        !t.isDeleted &&
        t.serviceType !== 'Sozlesmeli' &&
        !t.contractedCompanyId &&
        Boolean(getTicketReportDate(t) && getTicketReportDate(t).startsWith(mKey))
      );

      let rev = 0;
      let vat = 0;
      let exVatRev = 0;
      let collected = 0;
      let partsCost = 0;
      let subCost = 0;
      serviceMonthTickets.forEach(t => {
        const { grandTotal } = calculateTicketTotal(
          t.parts,
          t.laborCost,
          t.vatRate,
          t.discount,
          t.isVatIncluded, t.roundingAdjustment || 0
        );
        const pCost = calculateTicketPartsCost(t.parts);
        const sCost = t.subServiceCost || 0;
        partsCost += pCost;
        subCost += sCost;
      });

      tickets.forEach(t => {
        if (t.isDeleted || t.serviceType === 'Sozlesmeli' || t.contractedCompanyId) return;
        const received = getTicketPaymentSplitForPeriod(t, mKey).total;
        if (received <= 0) return;
        const { subtotal, vatAmount, grandTotal } = calculateTicketTotal(t.parts, t.laborCost, t.vatRate, t.discount, t.isVatIncluded, t.roundingAdjustment || 0);
        const ratio = grandTotal > 0 ? received / grandTotal : 0;
        rev += received;
        vat += vatAmount * ratio;
        exVatRev += subtotal * ratio;
        collected += received;
      });

      const totalCost = partsCost + subCost;

      return {
        name: label,
        Ciro: rev,
        Tahsilat: collected,
        'Alt Servis': subCost,
        'Net Kâr': exVatRev - totalCost,
      };
    });
  }, [availableMonths, tickets]);

  // Filtered tickets in the list table
  const displayedTableTickets = useMemo(() => {
    const periodKey = reportMode === 'daily' ? selectedDayKey : selectedMonthKey;
    return currentReportTickets.filter(t => {
      const { grandTotal } = calculateTicketTotal(
        t.parts,
        t.laborCost,
        t.vatRate,
        t.discount,
        t.isVatIncluded, t.roundingAdjustment || 0
      );
      const periodSplit = getTicketPaymentSplitForPeriod(t, periodKey);
      const paid = periodSplit.total;
      const remaining = Math.max(0, grandTotal - getTicketPaymentSplit(t).total);
      const method = normalizePaymentMethod(t.paymentMethod);

      if (tableFilter === 'delivered') {
        return t.status === 'TeslimEdildi';
      }
      if (tableFilter === 'paid') {
        return paid > 0;
      }
      if (tableFilter === 'pending') {
        return remaining > 0;
      }
      if (tableFilter === 'cash') {
        return periodSplit.cash > 0;
      }
      if (tableFilter === 'card') {
        return periodSplit.card > 0;
      }
      if (tableFilter === 'transfer') {
        return periodSplit.transfer > 0;
      }
      return true;
    });
  }, [currentReportTickets, tableFilter, reportMode, selectedDayKey, selectedMonthKey]);

  // Stored users for technician mode
  const allUsers = useMemo(() => {
    return getStoredUsers().filter(u => u.isActive !== false);
  }, []);

  const [selectedTechId, setSelectedTechId] = useState<string>(() => {
    if (isTechnician && currentUser) {
      return currentUser.id || currentUser.username;
    }
    return 'all';
  });

  const activeTechnician = useMemo(() => {
    if (isTechnician && currentUser) return currentUser;
    if (selectedTechId === 'all') return null;
    return allUsers.find(u => u.id === selectedTechId || u.username === selectedTechId) || null;
  }, [isTechnician, currentUser, selectedTechId, allUsers]);

  const activeTechnicianDisplayName = useMemo(() => {
    if (isTechnician && currentUser) return currentUser.name || currentUser.username;
    if (activeTechnician) return activeTechnician.name || activeTechnician.username;
    if (selectedTechId === 'all') return 'Tüm Teknisyenler (Genel Döküm)';
    return selectedTechId;
  }, [isTechnician, currentUser, activeTechnician, selectedTechId]);

  const technicianFilteredTickets = useMemo(() => {
    return tickets.filter(t => {
      if (selectedMonthKey !== 'all') {
        const reportDate = getTicketReportDate(t);
        const inMonth = Boolean(reportDate && reportDate.startsWith(selectedMonthKey));
        if (!inMonth) return false;
      }
      if (isTechnician && currentUser) {
        return isTicketAssignedToUser(t, currentUser);
      }
      if (selectedTechId === 'all') return true;
      if (activeTechnician) return isTicketAssignedToUser(t, activeTechnician);
      return (
        t.assignedTechnicianId === selectedTechId ||
        t.assignedTechnicianUsername === selectedTechId ||
        t.assignedTechnicianName === selectedTechId
      );
    });
  }, [tickets, selectedMonthKey, isTechnician, currentUser, selectedTechId, activeTechnician]);

  // Export PDF Handler
  const handleExportPDF = () => {
    if (reportMode === 'daily') {
      exportDailyReportPDF(selectedDayKey, formattedDayName, tickets, companyInfo);
    } else if (reportMode === 'monthly') {
      exportMonthlyReportPDF(selectedMonthKey, formattedMonthName, tickets, companyInfo);
    } else if (reportMode === 'collections') {
      exportMonthlyCollectionsReportPDF(selectedMonthKey, formattedMonthName, tickets, companyInfo);
    } else if (reportMode === 'stock') {
      exportStockUsageReportPDF(
        selectedStockPeriod,
        formattedStockPeriodName,
        tickets,
        inventory,
        companyInfo
      );
    } else if (reportMode === 'technician') {
      exportTechnicianReportPDF(
        activeTechnicianDisplayName,
        formattedMonthName,
        technicianFilteredTickets,
        companyInfo,
        selectedMonthKey
      );
    }
  };

  // Export Excel Handler
  const handleExportExcel = () => {
    if (reportMode === 'daily') {
      exportDailyReportExcel(selectedDayKey, formattedDayName, tickets, companyInfo);
    } else if (reportMode === 'monthly') {
      exportMonthlyReportExcel(selectedMonthKey, formattedMonthName, tickets, companyInfo);
    } else if (reportMode === 'stock') {
      exportStockUsageReportExcel(
        selectedStockPeriod,
        formattedStockPeriodName,
        tickets,
        inventory,
        companyInfo
      );
    } else if (reportMode === 'technician') {
      exportTechnicianReportExcel(
        activeTechnicianDisplayName,
        formattedMonthName,
        technicianFilteredTickets,
        companyInfo,
        selectedMonthKey
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Banner, Mode Selector & Date Controls */}
      <div className="bg-white p-5 lg:p-6 rounded-2xl border border-slate-200/80 shadow-sm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
          {/* Left Column: Stacked Vertical Mode Selector (Alt Alta Sıralı Butonlar) */}
          <div className="lg:col-span-4 xl:col-span-3">
            <div className="flex flex-col gap-1.5 p-1.5 bg-slate-100/90 rounded-2xl border border-slate-200/90">
              <button
                onClick={() => setReportMode('daily')}
                className={`w-full px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-between cursor-pointer ${
                  reportMode === 'daily'
                    ? 'bg-white text-blue-700 shadow-sm border border-slate-200/60'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'
                }`}
              >
                <div className="flex items-center space-x-2.5">
                  <CalendarDays className="w-4 h-4 text-blue-600 shrink-0" />
                  <span>Günlük Rapor</span>
                </div>
                {reportMode === 'daily' && <Check className="w-3.5 h-3.5 text-blue-600" />}
              </button>

              <button
                onClick={() => setReportMode('monthly')}
                className={`w-full px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-between cursor-pointer ${
                  reportMode === 'monthly'
                    ? 'bg-white text-blue-700 shadow-sm border border-slate-200/60'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'
                }`}
              >
                <div className="flex items-center space-x-2.5">
                  <BarChart3 className="w-4 h-4 text-blue-600 shrink-0" />
                  <span>Aylık Rapor</span>
                </div>
                {reportMode === 'monthly' && <Check className="w-3.5 h-3.5 text-blue-600" />}
              </button>

              {!isLimitedTechnician && (
                <button
                  onClick={() => setReportMode('collections')}
                  className={`w-full px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-between cursor-pointer ${
                    reportMode === 'collections'
                      ? 'bg-white text-emerald-700 shadow-sm border border-slate-200/60'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'
                  }`}
                >
                  <div className="flex items-center space-x-2.5">
                    <Coins className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>Aylık Tahsilat</span>
                  </div>
                  {reportMode === 'collections' && <Check className="w-3.5 h-3.5 text-emerald-600" />}
                </button>
              )}

              {!isLimitedTechnician && (
                <button
                  onClick={() => setReportMode('printerMonthly')}
                  className={`w-full px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-between cursor-pointer ${
                    reportMode === 'printerMonthly'
                      ? 'bg-white text-cyan-700 shadow-sm border border-slate-200/60'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'
                  }`}
                >
                  <div className="flex items-center space-x-2.5">
                    <PackageCheck className="w-4 h-4 text-cyan-600 shrink-0" />
                    <span>Yazıcı Aylık</span>
                  </div>
                  {reportMode === 'printerMonthly' && <Check className="w-3.5 h-3.5 text-cyan-600" />}
                </button>
              )}

              {!isLimitedTechnician && (
                <>
                  <button
                    onClick={() => setReportMode('technician')}
                    className={`w-full px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-between cursor-pointer ${
                      reportMode === 'technician'
                        ? 'bg-white text-indigo-700 shadow-sm border border-slate-200/60'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'
                    }`}
                  >
                    <div className="flex items-center space-x-2.5">
                      <UserCheck className="w-4 h-4 text-indigo-600 shrink-0" />
                      <span>Teknisyen Raporu</span>
                    </div>
                    {reportMode === 'technician' && <Check className="w-3.5 h-3.5 text-indigo-600" />}
                  </button>

                  <button
                    onClick={() => setReportMode('stock')}
                    className={`w-full px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-between cursor-pointer ${
                      reportMode === 'stock'
                        ? 'bg-white text-amber-700 shadow-sm border border-slate-200/60'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'
                    }`}
                  >
                    <div className="flex items-center space-x-2.5">
                      <PackageCheck className="w-4 h-4 text-amber-600 shrink-0" />
                      <span>Stok Düşüm Raporu</span>
                    </div>
                    {reportMode === 'stock' && <Check className="w-3.5 h-3.5 text-amber-600" />}
                  </button>

                  <button
                    onClick={() => setReportMode('contracted')}
                    className={`w-full px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-between cursor-pointer ${
                      reportMode === 'contracted'
                        ? 'bg-white text-purple-700 shadow-sm border border-slate-200/60'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/50'
                    }`}
                  >
                    <div className="flex items-center space-x-2.5">
                      <Building2 className="w-4 h-4 text-purple-600 shrink-0" />
                      <span>Sözleşmeli Firmalar</span>
                    </div>
                    {reportMode === 'contracted' && <Check className="w-3.5 h-3.5 text-purple-600" />}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Right Column: Title, Subtitle, and Unified Toolbar */}
          <div className="lg:col-span-8 xl:col-span-9 flex flex-col justify-between h-full gap-4">
            {/* Title & Info Section */}
            <div className="flex items-start gap-3.5">
              <div className={`p-3 rounded-2xl border shrink-0 ${
                reportMode === 'stock'
                  ? 'bg-amber-50 text-amber-700 border-amber-200'
                  : reportMode === 'printerMonthly'
                  ? 'bg-cyan-50 text-cyan-700 border-cyan-200'
                  : reportMode === 'technician'
                  ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                  : reportMode === 'contracted'
                  ? 'bg-purple-50 text-purple-700 border-purple-200'
                  : reportMode === 'collections'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-blue-50 text-blue-700 border-blue-100'
              }`}>
                {reportMode === 'printerMonthly' ? (
                  <PackageCheck className="w-6 h-6" />
                ) : reportMode === 'stock' ? (
                  <PackageCheck className="w-6 h-6" />
                ) : reportMode === 'technician' ? (
                  <UserCheck className="w-6 h-6" />
                ) : reportMode === 'contracted' ? (
                  <Building2 className="w-6 h-6" />
                ) : reportMode === 'collections' ? (
                  <Coins className="w-6 h-6" />
                ) : (
                  <TrendingUp className="w-6 h-6" />
                )}
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 leading-tight">
                  {reportMode === 'printerMonthly'
                    ? 'Yazıcı Aylık Raporu'
                    : reportMode === 'stock'
                    ? 'Kullanılan Parçalar & Stok Düşüm Raporu'
                    : reportMode === 'technician'
                    ? 'Teknisyen Performans & Hakediş Raporu'
                    : reportMode === 'contracted'
                    ? 'Sözleşmeli Kurumsal Firmalar & Bakiye Kâr/Zarar Takibi'
                    : reportMode === 'collections'
                    ? 'Aylık Tahsilat Raporu'
                    : isLimitedTechnician
                    ? 'Günlük & Aylık Servis ve Tahsilat Raporları'
                    : 'Finansal Servis & Kasa Raporları'}
                </h2>
                <p className="text-xs text-slate-500 mt-1 min-h-[18px]">
                  {reportMode === 'printerMonthly'
                    ? 'Yazıcı servislerinin aylık stok, parça, garanti ve hakediş özeti'
                    : reportMode === 'stock'
                    ? 'Servislerde kullanılan yedek parçaların dökümü, depodan stok düşüşleri ve parça kârlılık analizi'
                    : reportMode === 'technician'
                    ? 'Teknisyenlerin baktığı cihazlar, tahsilat dağılımı (nakit/kart/EFT) ve hakediş analizi'
                    : reportMode === 'contracted'
                    ? 'Kurumsal anlaşmalı firmaların servis limitleri, tüketilen bakım tutarları, parça maliyetleri ve ay sonu kâr/zarar analizi'
                    : reportMode === 'collections'
                    ? 'Seçilen ay içinde gerçekten alınan ödemeler; servis işlem tarihinden bağımsız olarak ödeme gününe göre listelenir.'
                    : isLimitedTechnician
                    ? 'Form ücretleri son servis işlem gününe/ayına göre; tahsilatlar paranın alındığı tarihe göre ayrıca gösterilir.'
                    : 'Form ücretleri son servis işlem tarihine göre; tahsilatlar ise paranın alındığı tarihe göre raporlanır.'}
                </p>
              </div>
            </div>

            {/* Unified Stable Controls Bar (Date / Month / Tech selector + PDF / Excel buttons) */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-100">
              <div className="flex flex-wrap items-center gap-2.5">
                {/* Period / Date Picker */}
                {reportMode === 'daily' ? (
                  <div className="flex items-center space-x-1.5 bg-slate-50 px-2.5 py-1 rounded-xl border border-slate-200 shrink-0 h-[38px] min-w-[170px]">
                    <Calendar className="w-4 h-4 text-slate-500 shrink-0" />
                    <input
                      type="date"
                      value={selectedDayKey}
                      onChange={e => e.target.value && setSelectedDayKey(e.target.value)}
                      className="w-full bg-white border border-slate-200 text-xs font-bold text-slate-800 px-2 py-1 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer shadow-sm"
                    />
                  </div>
                ) : reportMode === 'monthly' || reportMode === 'printerMonthly' || reportMode === 'collections' ? (
                  <div className="flex items-center space-x-1.5 bg-slate-50 px-2.5 py-1 rounded-xl border border-slate-200 shrink-0 h-[38px] min-w-[170px]">
                    <Calendar className="w-4 h-4 text-slate-500 shrink-0" />
                    <select
                      value={selectedMonthKey}
                      onChange={e => setSelectedMonthKey(e.target.value)}
                      className="w-full bg-white border border-slate-200 px-2.5 py-1 rounded-lg text-xs font-bold text-slate-900 focus:outline-none cursor-pointer shadow-sm"
                    >
                      {availableMonths.map(mKey => {
                        const [yr, mo] = mKey.split('-');
                        return (
                          <option key={mKey} value={mKey}>
                            {MONTH_NAMES_TR[mo] || mo} {yr}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                ) : reportMode === 'technician' ? (
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Month Selector for Tech Mode */}
                    <div className="flex items-center space-x-1.5 bg-slate-50 px-2.5 py-1 rounded-xl border border-slate-200 shrink-0 h-[38px] min-w-[150px]">
                      <Calendar className="w-4 h-4 text-slate-500 shrink-0" />
                      <select
                        value={selectedMonthKey}
                        onChange={e => setSelectedMonthKey(e.target.value)}
                        className="w-full bg-white border border-slate-200 px-2.5 py-1 rounded-lg text-xs font-bold text-slate-900 focus:outline-none cursor-pointer shadow-sm"
                      >
                        <option value="all">Tüm Zamanlar</option>
                        {availableMonths.map(mKey => {
                          const [yr, mo] = mKey.split('-');
                          return (
                            <option key={mKey} value={mKey}>
                              {MONTH_NAMES_TR[mo] || mo} {yr}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    {/* Technician Dropdown for Admins in Tech Mode */}
                    {!isTechnician && (
                      <div className="flex items-center space-x-1.5 bg-slate-50 px-2.5 py-1 rounded-xl border border-slate-200 shrink-0 h-[38px] min-w-[170px]">
                        <User className="w-4 h-4 text-indigo-600 shrink-0" />
                        <select
                          value={selectedTechId}
                          onChange={e => setSelectedTechId(e.target.value)}
                          className="w-full bg-white border border-slate-200 px-2.5 py-1 rounded-lg text-xs font-bold text-slate-900 focus:outline-none cursor-pointer shadow-sm"
                        >
                          <option value="all">Tüm Teknisyenler</option>
                          {allUsers.map(u => (
                            <option key={u.id} value={u.id}>
                              {u.name} ({u.role === 'technician' ? 'Teknisyen' : 'Yönetici'})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                ) : reportMode === 'stock' ? (
                  /* Stock mode period selector */
                  <div className="flex items-center space-x-1.5 bg-slate-50 px-2.5 py-1 rounded-xl border border-slate-200 shrink-0 h-[38px] min-w-[180px]">
                    <Calendar className="w-4 h-4 text-slate-500 shrink-0" />
                    <select
                      value={selectedStockPeriod}
                      onChange={e => setSelectedStockPeriod(e.target.value)}
                      className="w-full bg-white border border-slate-200 px-2.5 py-1 rounded-lg text-xs font-bold text-slate-900 focus:outline-none cursor-pointer shadow-sm"
                    >
                      <option value="all">Tüm Zamanlar / Tüm Aylar</option>
                      {availableMonths.map(mKey => {
                        const [yr, mo] = mKey.split('-');
                        return (
                          <option key={mKey} value={mKey}>
                            {MONTH_NAMES_TR[mo] || mo} {yr}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                ) : null}
              </div>

              {/* Universal Grouped Export Buttons */}
              {reportMode !== 'printerMonthly' && <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={handleExportPDF}
                  title="PDF Raporunu İndir"
                  className="px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl shadow-sm flex items-center space-x-1.5 transition-all hover:scale-[1.02] cursor-pointer whitespace-nowrap h-[38px]"
                >
                  <Download className="w-3.5 h-3.5 text-emerald-400" />
                  <span>
                    {reportMode === 'daily'
                      ? 'Günlük PDF'
                      : reportMode === 'monthly'
                      ? 'Aylık PDF'
                      : reportMode === 'technician'
                      ? 'Teknisyen PDF'
                      : reportMode === 'contracted'
                      ? 'Firma PDF'
                      : reportMode === 'collections'
                      ? 'Tahsilat PDF'
                      : 'Stok PDF'}
                  </span>
                </button>

                {reportMode !== 'collections' && <button
                  onClick={handleExportExcel}
                  title="Excel Tablosu (.xlsx) Olarak İndir"
                  className="px-3.5 py-2 bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-bold rounded-xl shadow-sm flex items-center space-x-1.5 transition-all hover:scale-[1.02] cursor-pointer whitespace-nowrap h-[38px]"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-200" />
                  <span>Excel (.xlsx)</span>
                </button>}
              </div>}
            </div>
          </div>
        </div>
      </div>

      {/* Conditionally Render Stock Usage Section OR Technician Report Section OR Contracted Section OR Financial Dashboard */}
      {reportMode === 'printerMonthly' ? (
        <PrinterMonthlyReportSection
          tickets={tickets}
          inventory={inventory}
          stockMovements={stockMovements}
          companyInfo={companyInfo}
          month={selectedMonthKey}
          canEditSettlement={currentUser?.role === 'admin'}
          onSaveCompanyInfo={onSaveCompanyInfo}
        />
      ) : reportMode === 'collections' ? (
        <CollectionsTransactionsSection
          tickets={tickets}
          periodKey={selectedMonthKey}
          periodName={formattedMonthName}
          onExportPDF={handleExportPDF}
        />
      ) : reportMode === 'stock' ? (
        <StockUsageReportSection
          tickets={tickets}
          inventory={inventory}
          stockMovements={stockMovements}
          companyInfo={companyInfo}
          selectedPeriod={selectedStockPeriod}
          onPeriodChange={setSelectedStockPeriod}
          availableMonths={availableMonths}
          onExportPDF={handleExportPDF}
          onExportExcel={handleExportExcel}
        />
      ) : reportMode === 'technician' ? (
        <TechnicianReportSection
          tickets={tickets}
          companyInfo={companyInfo}
          currentUser={currentUser}
          selectedMonth={selectedMonthKey}
          onMonthChange={setSelectedMonthKey}
          availableMonths={availableMonths}
          selectedTechId={selectedTechId}
          onTechChange={setSelectedTechId}
        />
      ) : reportMode === 'contracted' ? (
        <ContractedCompaniesView
          tickets={tickets}
        />
      ) : (
        <>

      {/* Metric Cards */}
      <div className={`grid gap-4 ${isLimitedTechnician ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5'}`}>
        {/* 1. Tahsil Edilen (Ödemesi Alınan) */}
        <div className="bg-white rounded-2xl p-4.5 border border-slate-200/80 shadow-sm space-y-1.5 min-w-0 flex flex-col justify-between">
          <div>
            <div className="text-slate-500 text-xs font-semibold">
              <span>{reportMode === 'monthly' ? 'Aylık Tahsilat (Alınan)' : 'Günlük Tahsilat (Alınan)'}</span>
            </div>
            <h3 className="text-xl font-bold font-mono text-emerald-600 mt-1">{formatTL(metrics.totalCollected)}</h3>
          </div>
          <div className="flex items-center justify-between text-[11px] text-slate-400 pt-2 border-t border-slate-100">
            <span>{reportMode === 'monthly' ? `${formattedMonthName} Girişi` : 'Günlük Kasa Girişi'}</span>
            <span className="font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded whitespace-nowrap">
              %{metrics.collectionRate} Tahsilat
            </span>
          </div>
        </div>

        {/* 2. Alınacak Ücretler (Kalan Borç / Bekleyen Alacak) */}
        <div className="bg-white rounded-2xl p-4.5 border border-slate-200/80 shadow-sm space-y-1.5 min-w-0 flex flex-col justify-between">
          <div>
            <div className="text-slate-500 text-xs font-semibold">
              <span>{reportMode === 'monthly' ? 'Ay İçinde Alınacak' : 'Günlük Kalan Alacak'}</span>
            </div>
            <h3 className="text-xl font-bold font-mono text-amber-700 mt-1">{formatTL(metrics.totalRemaining)}</h3>
          </div>
          <div className="flex items-center justify-between text-[11px] text-slate-400 pt-2 border-t border-slate-100">
            <span>{reportMode === 'monthly' ? `${formattedMonthName} servis formlarının bakiyesi` : 'Bekleyen Bakiye'}</span>
            <span className="font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded whitespace-nowrap">
              {metrics.pendingPaymentCount} Kayıt
            </span>
          </div>
        </div>

        {/* 3. Teslim Edilen Cihazlar */}
        <div className="bg-white rounded-2xl p-4.5 border border-slate-200/80 shadow-sm space-y-1.5 min-w-0 flex flex-col justify-between">
          <div>
            <div className="text-slate-500 text-xs font-semibold">
              <span>{reportMode === 'monthly' ? 'Aylık Teslim Edilenler' : 'Günlük Teslim Edilenler'}</span>
            </div>
            <div className="flex flex-wrap items-baseline gap-1.5 mt-1">
              <h3 className="text-xl font-bold text-slate-900">{metrics.deliveredCount} Cihaz</h3>
              {!isLimitedTechnician && (
                <span className="text-[11px] font-bold font-mono text-blue-600">({formatTL(metrics.deliveredRevenue)})</span>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between text-[11px] text-slate-400 pt-2 border-t border-slate-100">
            <span>{reportMode === 'monthly' ? `${formattedMonthName} Teslimatı` : 'Müşteriye Teslim'}</span>
            <span className="font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded whitespace-nowrap">
              %{metrics.deliveryRate} Teslimat
            </span>
          </div>
        </div>

        {!isLimitedTechnician && (
          <>
            {/* 4. Toplam Servis Cirosu & KDV */}
            <div className="bg-white rounded-2xl p-4.5 border border-slate-200/80 shadow-sm space-y-1.5 min-w-0 flex flex-col justify-between">
              <div>
                <div className="text-slate-500 text-xs font-semibold">
              <span>{reportMode === 'monthly' ? 'Aylık Tahsilat Matrahı (KDV Hariç)' : 'Günlük Tahsilat Matrahı (KDV Hariç)'}</span>
                </div>
                <h3 className="text-xl font-bold font-mono text-slate-900 mt-1">{formatTL(metrics.totalExVatRevenue)}</h3>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-1 text-[11px] text-slate-500 font-medium pt-2 border-t border-slate-100">
                <span>KDV: <b className="text-purple-700 font-mono">{formatTL(metrics.totalVat)}</b></span>
                <span>KDV dahil: <b className="text-slate-700 font-mono">{formatTL(metrics.totalRevenue)}</b></span>
              </div>
            </div>

            {/* 5. Net Kâr */}
            <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 text-white rounded-2xl p-4.5 shadow-md space-y-2.5 relative overflow-hidden border border-slate-700 min-w-0 flex flex-col justify-between sm:col-span-2 lg:col-span-1">
              <div>
                <div className="text-slate-300 text-xs font-semibold">
                  <span>{reportMode === 'monthly' ? 'Aylık Net Kâr' : 'Günlük Net Kâr'}</span>
                </div>
                <h3 className="text-2xl font-black font-mono text-emerald-400 tracking-tight mt-1">{formatTL(metrics.netProfitExVat)}</h3>
              </div>
              <div className="pt-2 border-t border-slate-700/80 space-y-1.5 text-xs text-slate-300">
                <div className="bg-slate-800/90 rounded-lg px-2.5 py-1.5 border border-slate-700/70 flex items-center justify-between gap-2">
                  <span className="text-[11px] text-slate-400 font-medium">Brüt Kâr:</span>
                  <span className="font-bold text-slate-100 font-mono text-xs whitespace-nowrap">{formatTL(metrics.grossProfit)}</span>
                </div>
                <div className="bg-purple-950/60 rounded-lg px-2.5 py-1.5 border border-purple-800/50 flex items-center justify-between gap-2">
                  <span className="text-[11px] text-purple-300 font-medium">KDV:</span>
                  <span className="font-bold text-purple-300 font-mono text-xs whitespace-nowrap">-{formatTL(metrics.totalVat)}</span>
                </div>
                <div className="bg-rose-950/60 rounded-lg px-2.5 py-1.5 border border-rose-800/50 flex items-center justify-between gap-2">
                  <span className="text-[11px] text-rose-300 font-medium">Parça:</span>
                  <span className="font-bold text-rose-300 font-mono text-xs whitespace-nowrap">-{formatTL(metrics.totalPartsCost)}</span>
                </div>
                {metrics.totalSubServiceCost > 0 && (
                  <div className="bg-amber-950/60 rounded-lg px-2.5 py-1.5 border border-amber-800/50 flex items-center justify-between gap-2">
                    <span className="text-[11px] text-amber-300 font-medium">Alt Servis:</span>
                    <span className="font-bold text-amber-300 font-mono text-xs whitespace-nowrap">-{formatTL(metrics.totalSubServiceCost)}</span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {reportMode === 'daily' && (
        <CollectionsTransactionsSection
          tickets={tickets}
          periodKey={selectedDayKey}
          periodName={formattedDayName}
          onExportPDF={handleExportPDF}
        />
      )}

      {!isLimitedTechnician && (reportMode === 'monthly' || reportMode === 'daily') && (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 sm:p-6 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <div>
              <h3 className="font-bold text-sm sm:text-base text-slate-900">Cihaz Alanı Gelir Raporu</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Form ücretleri son servis işlem tarihine göre, tahsilatlar paranın alındığı tarihe göre gösterilir.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <select
                value={selectedMonthKey}
                onChange={e => {
                  setSelectedMonthKey(e.target.value);
                  setReportMode('monthly');
                }}
                className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-xs font-bold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                {availableMonths.map(month => {
                  const [year, monthNo] = month.split('-');
                  return <option key={month} value={month}>{MONTH_NAMES_TR[monthNo] || monthNo} {year}</option>;
                })}
              </select>
              <button
                type="button"
                onClick={() => exportDeviceAreaMonthlyReportPDF(selectedMonthKey, formattedMonthName, tickets, companyInfo)}
                className="inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold shadow-sm transition-colors cursor-pointer"
              >
                <FileText className="w-4 h-4" />
                Aylık PDF Al
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-black text-blue-950">Yazıcı / Tarayıcı</span>
                <span className="text-[11px] font-bold text-blue-700 bg-white border border-blue-200 rounded-lg px-2 py-1">{deviceAreaReport.printer.count} İş</span>
              </div>
              <div className="text-xs font-semibold text-slate-500 mt-3">Dönem Tahsilatı</div>
              <div className="text-2xl font-black font-mono text-blue-700">{formatTL(deviceAreaReport.printer.revenue)}</div>
              <div className="grid grid-cols-1 gap-2 mt-3 text-[11px]">
                <div className="bg-white rounded-lg border border-blue-100 p-2"><span className="text-slate-500">Kalan</span><div className="font-bold font-mono text-amber-700">{formatTL(deviceAreaReport.printer.remaining)}</div></div>
              </div>
            </div>
            <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-black text-indigo-950">Bilgisayar</span>
                <span className="text-[11px] font-bold text-indigo-700 bg-white border border-indigo-200 rounded-lg px-2 py-1">{deviceAreaReport.computer.count} İş</span>
              </div>
              <div className="text-xs font-semibold text-slate-500 mt-3">Dönem Tahsilatı</div>
              <div className="text-2xl font-black font-mono text-indigo-700">{formatTL(deviceAreaReport.computer.revenue)}</div>
              <div className="grid grid-cols-1 gap-2 mt-3 text-[11px]">
                <div className="bg-white rounded-lg border border-indigo-100 p-2"><span className="text-slate-500">Kalan</span><div className="font-bold font-mono text-amber-700">{formatTL(deviceAreaReport.computer.remaining)}</div></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DEDICATED PAYMENT METHOD BREAKDOWN PANEL: KASA GİRİŞLERİ (NAKİT / KART / HAVALE) */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 sm:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b pb-3 border-slate-100">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-indigo-50 text-indigo-700 rounded-xl border border-indigo-100 shrink-0">
              <Coins className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-sm sm:text-base text-slate-900 flex items-center gap-2">
                <span>Tahsilat & Kasa Dağılımı (Ödeme Türleri)</span>
                <span className="text-xs text-slate-400 font-normal">
                  ({reportMode === 'monthly' ? formattedMonthName : formattedDayName})
                </span>
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Alınan toplam <b className="text-slate-800">{formatTL(paymentBreakdown.totalCollected)}</b> tahsilatın nakit, kart ve havale kırılımı
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 self-start sm:self-auto bg-slate-50 px-3 py-1 rounded-xl border border-slate-200 text-xs">
            <span className="text-slate-500">Toplam Tahsilat:</span>
            <span className="font-bold text-emerald-600 font-mono text-sm">{formatTL(paymentBreakdown.totalCollected)}</span>
          </div>
        </div>

        {/* 3 Prominent Payment Method Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Card 1: Nakit Tahsilat */}
          <div
            onClick={() => setTableFilter(tableFilter === 'cash' ? 'all' : 'cash')}
            className={`rounded-xl p-4 border transition-all cursor-pointer relative overflow-hidden ${
              tableFilter === 'cash'
                ? 'bg-emerald-50 border-emerald-500 ring-2 ring-emerald-500/20 shadow-sm'
                : 'bg-emerald-50/40 hover:bg-emerald-50/70 border-emerald-200/80 hover:border-emerald-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center shadow-xs">
                  <Banknote className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-xs font-bold text-emerald-950 block">Nakit Kasa Girişi</span>
                  <span className="text-[11px] text-emerald-700 font-medium">Elden / Nakit Tahsilat</span>
                </div>
              </div>
            </div>

            <div className="mt-3 flex items-baseline justify-between">
              <div className="text-xl sm:text-2xl font-black text-emerald-700 font-mono tracking-tight">
                {formatTL(paymentBreakdown.cash.amount)}
              </div>
              <div className="text-xs font-semibold text-emerald-800">
                {paymentBreakdown.cash.count} Adet Kayıt
              </div>
            </div>

            <div className="mt-2 text-[11px] text-emerald-600/90 font-medium flex items-center justify-between border-t border-emerald-200/60 pt-2">
              <span>{tableFilter === 'cash' ? '✓ Filtrelendi' : 'Listeyi filtrelemek için tıklayın'}</span>
              <ArrowRightSmall />
            </div>
          </div>

          {/* Card 2: Kredi Kartı / POS Tahsilat */}
          <div
            onClick={() => setTableFilter(tableFilter === 'card' ? 'all' : 'card')}
            className={`rounded-xl p-4 border transition-all cursor-pointer relative overflow-hidden ${
              tableFilter === 'card'
                ? 'bg-indigo-50 border-indigo-500 ring-2 ring-indigo-500/20 shadow-sm'
                : 'bg-indigo-50/40 hover:bg-indigo-50/70 border-indigo-200/80 hover:border-indigo-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-lg bg-indigo-600 text-white flex items-center justify-center shadow-xs">
                  <CreditCard className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-xs font-bold text-indigo-950 block">Kredi Kartı / POS</span>
                  <span className="text-[11px] text-indigo-700 font-medium">Banka & Kredi Kartı</span>
                </div>
              </div>
            </div>

            <div className="mt-3 flex items-baseline justify-between">
              <div className="text-xl sm:text-2xl font-black text-indigo-700 font-mono tracking-tight">
                {formatTL(paymentBreakdown.card.amount)}
              </div>
              <div className="text-xs font-semibold text-indigo-800">
                {paymentBreakdown.card.count} Adet Kayıt
              </div>
            </div>

            <div className="mt-2 text-[11px] text-indigo-600/90 font-medium flex items-center justify-between border-t border-indigo-200/60 pt-2">
              <span>{tableFilter === 'card' ? '✓ Filtrelendi' : 'Listeyi filtrelemek için tıklayın'}</span>
              <ArrowRightSmall />
            </div>
          </div>

          {/* Card 3: Banka Havalesi / EFT Tahsilat */}
          <div
            onClick={() => setTableFilter(tableFilter === 'transfer' ? 'all' : 'transfer')}
            className={`rounded-xl p-4 border transition-all cursor-pointer relative overflow-hidden ${
              tableFilter === 'transfer'
                ? 'bg-cyan-50 border-cyan-500 ring-2 ring-cyan-500/20 shadow-sm'
                : 'bg-cyan-50/40 hover:bg-cyan-50/70 border-cyan-200/80 hover:border-cyan-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-lg bg-cyan-600 text-white flex items-center justify-center shadow-xs">
                  <Landmark className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-xs font-bold text-cyan-950 block">Hesaba Havale / EFT</span>
                  <span className="text-[11px] text-cyan-700 font-medium">Banka FAST / EFT Transferi</span>
                </div>
              </div>
            </div>

            <div className="mt-3 flex items-baseline justify-between">
              <div className="text-xl sm:text-2xl font-black text-cyan-700 font-mono tracking-tight">
                {formatTL(paymentBreakdown.transfer.amount)}
              </div>
              <div className="text-xs font-semibold text-cyan-800">
                {paymentBreakdown.transfer.count} Adet Kayıt
              </div>
            </div>

            <div className="mt-2 text-[11px] text-cyan-600/90 font-medium flex items-center justify-between border-t border-cyan-200/60 pt-2">
              <span>{tableFilter === 'transfer' ? '✓ Filtrelendi' : 'Listeyi filtrelemek için tıklayın'}</span>
              <ArrowRightSmall />
            </div>
          </div>
        </div>
      </div>

      {/* Visual Analytics Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {!isLimitedTechnician && (
          /* Left Chart: Financial Trend & Alt Service Breakdown */
          <div className="lg:col-span-7 bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b pb-3 border-slate-100">
              <div>
                <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-blue-600" />
                  {reportMode === 'monthly'
                    ? 'Son 6 Aylık Finansal Trend (Ciro, Tahsilat, Alt Servis & Net Kâr)'
                    : `Günün Finansal Akış Dengesi (${formattedDayName})`}
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  {reportMode === 'monthly'
                    ? 'Aylık ciro, tahsilat, alt servis giderleri ve net kâr karşılaştırması'
                    : 'Tahsil edilen, kalan alacak, alt servis ve parça giderleri ile net kâr'}
                </p>
              </div>
              {metrics.totalSubServiceCost > 0 && (
                <div className="flex items-center gap-1.5 self-start sm:self-auto bg-amber-50 text-amber-800 text-[11px] font-semibold px-2.5 py-1 rounded-lg border border-amber-200/70">
                  <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                  <span>Alt Servis Gideri: <b>{formatTL(metrics.totalSubServiceCost)}</b></span>
                </div>
              )}
            </div>

            <div className="h-68 w-full pt-1">
              {reportMode === 'monthly' ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sixMonthsTrendData} margin={{ top: 8, right: 10, left: 0, bottom: 0 }} barGap={3}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={val => `${val}₺`} />
                    <Tooltip
                      formatter={(value: number) => [formatTL(value), '']}
                      contentStyle={{ backgroundColor: '#0f172a', borderRadius: '10px', color: '#fff', fontSize: '11px', border: '1px solid #334155' }}
                    />
                    <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '6px' }} iconType="circle" iconSize={8} />
                    <Bar dataKey="Ciro" fill="#2563eb" radius={[4, 4, 0, 0]} maxBarSize={20} name="Toplam Ciro" />
                    <Bar dataKey="Tahsilat" fill="#0ea5e9" radius={[4, 4, 0, 0]} maxBarSize={20} name="Tahsilat" />
                    <Bar dataKey="Alt Servis" fill="#f59e0b" radius={[4, 4, 0, 0]} maxBarSize={20} name="Alt Servis Gideri" />
                    <Bar dataKey="Net Kâr" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={20} name="Net Kâr" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      { name: 'Tahsilat (Kasa)', Tutar: metrics.totalCollected, fill: '#0ea5e9' },
                      { name: 'Kalan Alacak', Tutar: metrics.totalRemaining, fill: '#f59e0b' },
                      { name: 'Parça Maliyeti', Tutar: metrics.totalPartsCost, fill: '#ef4444' },
                      ...(metrics.totalSubServiceCost > 0
                        ? [{ name: 'Alt Servis Gideri', Tutar: metrics.totalSubServiceCost, fill: '#f97316' }]
                        : []),
                      { name: 'KDV Tutarı', Tutar: metrics.totalVat, fill: '#8b5cf6' },
                      { name: 'Net Kâr', Tutar: metrics.netProfitExVat, fill: '#10b981' },
                    ]}
                    margin={{ top: 8, right: 10, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={val => `${val}₺`} />
                    <Tooltip
                      formatter={(value: number) => [formatTL(value), 'Tutar']}
                      contentStyle={{ backgroundColor: '#0f172a', borderRadius: '10px', color: '#fff', fontSize: '11px', border: '1px solid #334155' }}
                    />
                    <Bar dataKey="Tutar" radius={[4, 4, 0, 0]} maxBarSize={32} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        )}

        {/* Right Chart: Interactive Tabs for Payment Methods vs Device Category */}
        <div className={`${isLimitedTechnician ? 'lg:col-span-12' : 'lg:col-span-5'} bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm space-y-3`}>
          <div className="flex items-center justify-between border-b pb-3 border-slate-100">
            <div>
              <h3 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                <PieIcon className="w-4 h-4 text-emerald-600" />
                {chartType === 'payment' ? 'Kasa Ödeme Türü Payları' : 'Cihaz Türü Dağılımı'}
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                {reportMode === 'daily' ? formattedDayName : formattedMonthName}
              </p>
            </div>

            {/* Chart Type Toggle Tabs */}
            <div className="flex items-center p-0.5 bg-slate-100 rounded-lg border border-slate-200 text-[11px] font-bold">
              <button
                type="button"
                onClick={() => setChartType('payment')}
                className={`px-2 py-1 rounded-md transition-all cursor-pointer ${
                  chartType === 'payment'
                    ? 'bg-white text-indigo-700 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Ödeme Türü
              </button>
              <button
                type="button"
                onClick={() => setChartType('device')}
                className={`px-2 py-1 rounded-md transition-all cursor-pointer ${
                  chartType === 'device'
                    ? 'bg-white text-indigo-700 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Cihaz Türü
              </button>
            </div>
          </div>

          <div className="h-68 w-full">
            {chartType === 'payment' ? (
              paymentMethodPieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={paymentMethodPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {paymentMethodPieData.map((entry, index) => (
                        <Cell key={`pm-cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number, name: string) => [
                        `${formatTL(value)} (${paymentBreakdown.totalCollected > 0 ? Math.round((value / paymentBreakdown.totalCollected) * 100) : 0}%)`,
                        name,
                      ]}
                      contentStyle={{ backgroundColor: '#0f172a', borderRadius: '10px', color: '#fff', fontSize: '11px', border: '1px solid #334155' }}
                    />
                    <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '4px' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-slate-400 text-xs italic">
                  Bu döneme ait tahsilat kaydı bulunmuyor.
                </div>
              )
            ) : deviceCategoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={deviceCategoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {deviceCategoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number) => [formatTL(value), 'Tutar']}
                    contentStyle={{ backgroundColor: '#0f172a', borderRadius: '10px', color: '#fff', fontSize: '11px', border: '1px solid #334155' }}
                  />
                  <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '4px' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-slate-400 text-xs italic">
                Bu döneme ait cihaz verisi bulunmuyor.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Service Transactions List & Filtered Breakdown Table */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden p-6 space-y-4">
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3 border-b pb-4 border-slate-100">
          <div>
            <h3 className="font-bold text-base text-slate-900 flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-600" />
              {reportMode === 'daily' ? 'Günün Servis & Tahsilat Kayıtları' : `${formattedMonthName} Dönemi Servis Kayıtları`}
              <span className="text-xs font-semibold text-slate-400">({displayedTableTickets.length} Kayıt)</span>
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Teslim edilen, tahsilatı yapılan, KDV'si ve KDV düşülmüş net kârı hesaplanan tüm servis kayıtları
            </p>
          </div>

          {/* Table Quick Filters */}
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              onClick={() => setTableFilter('all')}
              className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-colors cursor-pointer ${
                tableFilter === 'all'
                  ? 'bg-slate-900 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Tümü ({currentReportTickets.length})
            </button>
            <button
              onClick={() => setTableFilter('delivered')}
              className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-colors cursor-pointer ${
                tableFilter === 'delivered'
                  ? 'bg-blue-600 text-white shadow-xs'
                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
              }`}
            >
              Teslim Edilen ({metrics.deliveredCount})
            </button>
            <button
              onClick={() => setTableFilter('paid')}
              className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-colors cursor-pointer ${
                tableFilter === 'paid'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
              }`}
            >
              Ödemesi Alınan ({metrics.fullyPaidCount})
            </button>
            <button
              onClick={() => setTableFilter('cash')}
              className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-colors cursor-pointer flex items-center gap-1 ${
                tableFilter === 'cash'
                  ? 'bg-emerald-700 text-white shadow-xs'
                  : 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
              }`}
            >
              <Banknote className="w-3.5 h-3.5" />
              <span>Nakit ({paymentBreakdown.cash.count})</span>
            </button>
            <button
              onClick={() => setTableFilter('card')}
              className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-colors cursor-pointer flex items-center gap-1 ${
                tableFilter === 'card'
                  ? 'bg-indigo-700 text-white shadow-xs'
                  : 'bg-indigo-50 text-indigo-800 hover:bg-indigo-100'
              }`}
            >
              <CreditCard className="w-3.5 h-3.5" />
              <span>Kredi Kartı ({paymentBreakdown.card.count})</span>
            </button>
            <button
              onClick={() => setTableFilter('transfer')}
              className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-colors cursor-pointer flex items-center gap-1 ${
                tableFilter === 'transfer'
                  ? 'bg-cyan-700 text-white shadow-xs'
                  : 'bg-cyan-50 text-cyan-800 hover:bg-cyan-100'
              }`}
            >
              <Landmark className="w-3.5 h-3.5" />
              <span>Havale/EFT ({paymentBreakdown.transfer.count})</span>
            </button>
            <button
              onClick={() => setTableFilter('pending')}
              className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-colors cursor-pointer ${
                tableFilter === 'pending'
                  ? 'bg-amber-600 text-white shadow-xs'
                  : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
              }`}
            >
              Alacak Bekleyen ({metrics.pendingPaymentCount})
            </button>
          </div>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                <th className="py-3 px-3">Servis Kodu</th>
                <th className="py-3 px-3">Tarih</th>
                <th className="py-3 px-3">Müşteri</th>
                <th className="py-3 px-3">Cihaz & Model</th>
                <th className="py-3 px-3">Servis Durumu</th>
                <th className="py-3 px-3">Ödeme Türü / Durumu</th>
                <th className="py-3 px-3 text-right">Tahsilat</th>
                <th className="py-3 px-3 text-right">Kalan Alacak</th>
                {!isLimitedTechnician && (
                  <>
                    <th className="py-3 px-3 text-right">Parça Maliyet</th>
                    <th className="py-3 px-3 text-right">KDV Tutarı</th>
                    <th className="py-3 px-3 text-right">Toplam Tutar</th>
                    <th className="py-3 px-3 text-right">KDV Düşülmüş Net Kâr</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs font-medium">
              {displayedTableTickets.length > 0 ? (
                displayedTableTickets.map(t => {
                  const isContract = t.serviceType === 'Sozlesmeli' || Boolean(t.contractedCompanyId);
                  const breakdown = getTicketContractBreakdown(t);
                  const { subtotal, vatAmount, grandTotal } = calculateTicketTotal(
                    t.parts,
                    t.laborCost,
                    t.vatRate,
                    t.discount,
                    t.isVatIncluded, t.roundingAdjustment || 0
                  );
                  const pCost = calculateTicketPartsCost(t.parts);
                  const subCost = t.subServiceCost || 0;
                  const ticketTotalCost = pCost + subCost;
                  const paymentPeriod = reportMode === 'daily' ? selectedDayKey : selectedMonthKey;
                  const split = getTicketPaymentSplit(t);
                  const periodSplit = getTicketPaymentSplitForPeriod(t, paymentPeriod);
                  const paid = periodSplit.total;
                  const remaining = isContract ? breakdown.effectiveDebt : Math.max(0, grandTotal - getTicketPaymentSplit(t).total);
                  const paidRatio = grandTotal > 0 ? paid / grandTotal : 0;
                  const ticketGrossProfit = paid - ticketTotalCost;
                  const ticketNetProfitExVat = subtotal * paidRatio - ticketTotalCost; // Tahsil edilen KDV hariç gelir - maliyet
                  const isDelivered = t.status === 'TeslimEdildi';
                  const method = periodSplit.card > 0 ? 'Kredi Karti' : periodSplit.transfer > 0 ? 'EFT/Havale' : 'Nakit';

                  return (
                    <tr key={t.id} className="hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-3 font-mono font-bold text-blue-700 whitespace-nowrap">
                        {t.id}
                      </td>
                      <td className="py-3 px-3 text-slate-500 whitespace-nowrap">
                        <div>Kayıt: {t.createdAt ? t.createdAt.substring(0, 10) : '-'}</div>
                        {t.deliveryDate && (
                          <div className="text-[10px] text-emerald-700 font-semibold">
                            Teslim: {t.deliveryDate.substring(0, 10)}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-3 text-slate-900 font-bold">
                        <div>{t.customerName}</div>
                        <div className="text-[11px] text-slate-400 font-normal font-mono">{t.customerPhone}</div>
                      </td>
                      <td className="py-3 px-3 text-slate-700">
                        <span className="font-semibold text-slate-800">{t.deviceType}</span>
                        <div className="text-[11px] text-slate-500">{t.deviceBrand} {t.deviceModel}</div>
                        {t.serviceType && t.serviceType !== 'Ucretli' && (
                          <div className="mt-1">
                            {isContract ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-purple-700 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded">
                                <Building2 className="w-2.5 h-2.5" /> Sözleşmeli Cari
                              </span>
                            ) : t.serviceType === 'Garantili' ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                                Garantili
                              </span>
                            ) : t.serviceType === 'DisServis' ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-cyan-700 bg-cyan-50 border border-cyan-200 px-1.5 py-0.5 rounded">
                                Dış Servis
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                                Servis Garantisi / İş Takibi
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-3 whitespace-nowrap">
                        {isDelivered ? (
                          <span className="inline-flex items-center gap-1 text-emerald-800 font-bold bg-emerald-100 border border-emerald-300 px-2 py-0.5 rounded-md text-[11px]">
                            <PackageCheck className="w-3 h-3" />
                            Teslim Edildi
                          </span>
                        ) : (
                          <span className="text-slate-700 font-medium bg-slate-100 px-2 py-0.5 rounded-md text-[11px]">
                            {STATUS_LABELS[t.status] || t.status}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-3 whitespace-nowrap">
                        {isContract ? (
                          !breakdown.hasOutOfContractParts ? (
                            <span className="inline-flex items-center gap-1 text-purple-800 font-bold bg-purple-50 border border-purple-200 px-2 py-0.5 rounded text-[11px]">
                              <Building2 className="w-3 h-3 text-purple-600" />
                              Sözleşme Kapsamında (Ödendi)
                            </span>
                          ) : breakdown.effectiveDebt <= 0 ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 font-bold bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded text-[11px]">
                              <Check className="w-3 h-3" />
                              Harici Ödendi ({split.card > 0 ? 'Kart' : split.transfer > 0 ? 'Havale' : 'Nakit'})
                            </span>
                          ) : paid > 0 ? (
                            <span className="inline-flex items-center gap-1 text-amber-800 font-bold bg-amber-50 border border-amber-200 px-2 py-0.5 rounded text-[11px]">
                              <Clock className="w-3 h-3 text-amber-600" />
                              Harici Kısmi ({formatTL(paid)})
                            </span>
                          ) : (
                            <span className="text-rose-700 font-bold bg-rose-50 border border-rose-200 px-2 py-0.5 rounded text-[11px]">
                              Harici Bekliyor ({formatTL(breakdown.outOfContractTotal)})
                            </span>
                          )
                        ) : (t.isUnderWarranty || t.serviceType === 'Garantili' || t.serviceType === 'ServisGarantili') && grandTotal <= 0 ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 font-bold bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded text-[11px]">
                            Garanti kapsamında · Ücret yok
                          </span>
                        ) : t.paymentStatus === 'Odendi' ? (
                          method === 'Kredi Karti' ? (
                            <span className="inline-flex items-center gap-1 text-indigo-700 font-bold bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded text-[11px]">
                              <CreditCard className="w-3 h-3" />
                              Ödendi (Kart)
                            </span>
                          ) : method === 'EFT/Havale' ? (
                            <span className="inline-flex items-center gap-1 text-cyan-800 font-bold bg-cyan-50 border border-cyan-200 px-2 py-0.5 rounded text-[11px]">
                              <Landmark className="w-3 h-3" />
                              Ödendi (Havale)
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-emerald-700 font-bold bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded text-[11px]">
                              <Banknote className="w-3 h-3" />
                              Ödendi (Nakit)
                            </span>
                          )
                        ) : t.paymentStatus === 'KismiOdendi' ? (
                          <span className="inline-flex items-center gap-1 text-amber-800 font-bold bg-amber-50 border border-amber-200 px-2 py-0.5 rounded text-[11px]">
                            <Clock className="w-3 h-3 text-amber-600" />
                            Kısmi ({method === 'Kredi Karti' ? 'Kart' : method === 'EFT/Havale' ? 'Havale' : 'Nakit'})
                          </span>
                        ) : (
                          <span className="text-rose-700 font-bold bg-rose-50 border border-rose-200 px-2 py-0.5 rounded text-[11px]">
                            Bekliyor (Ödenmedi)
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-3 text-right font-mono font-bold text-emerald-600 whitespace-nowrap">
                        {isContract ? (
                          !breakdown.hasOutOfContractParts ? (
                            <span className="text-[11px] font-semibold text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded">Sözleşmeden ({formatTL(grandTotal)})</span>
                          ) : (
                            <div>
                              <div className="text-[10px] text-purple-700">Mahsup: {formatTL(breakdown.inContractGrandTotal)}</div>
                              <div className="text-emerald-700 font-bold font-mono">Harici: {formatTL(paid)}</div>
                            </div>
                          )
                        ) : (
                          formatTL(paid)
                        )}
                      </td>
                      <td className="py-3 px-3 text-right font-mono font-bold text-amber-700 whitespace-nowrap">
                        {isContract ? (
                          breakdown.effectiveDebt > 0 ? (
                            formatTL(breakdown.effectiveDebt)
                          ) : (
                            <span className="text-slate-400 font-normal">-</span>
                          )
                        ) : remaining > 0 ? (
                          formatTL(remaining)
                        ) : (
                          <span className="text-slate-400 font-normal">-</span>
                        )}
                      </td>
                      {!isLimitedTechnician && (
                        <>
                          <td className="py-3 px-3 text-right font-mono text-rose-600 whitespace-nowrap">
                            {formatTL(pCost)}
                          </td>
                          <td className="py-3 px-3 text-right font-mono whitespace-nowrap">
                            <div className="font-semibold text-purple-700">{formatTL(vatAmount * paidRatio)}</div>
                            <div className="text-[10px] text-slate-400">%20</div>
                          </td>
                          <td className="py-3 px-3 text-right font-mono font-bold text-slate-900 whitespace-nowrap">
                            {formatTL(grandTotal)}
                          </td>
                          <td className="py-3 px-3 text-right font-mono font-bold whitespace-nowrap">
                            <div className="text-emerald-700 font-black">{formatTL(ticketNetProfitExVat)}</div>
                            <div className="text-[10px] text-slate-400 font-normal">Brüt: {formatTL(ticketGrossProfit)}</div>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={isLimitedTechnician ? 8 : 12} className="py-12 text-center text-slate-400 italic">
                    Bu filtreleme veya seçilen döneme ait herhangi bir servis kaydı bulunamadı.
                  </td>
                </tr>
              )}
            </tbody>
            {displayedTableTickets.length > 0 && (
              <tfoot>
                <tr className="bg-slate-100 border-t-2 border-slate-300 text-xs font-bold text-slate-900">
                  <td colSpan={6} className="py-3 px-3 text-right">
                    Tablo Toplamları:
                  </td>
                  <td className="py-3 px-3 text-right font-mono text-emerald-700">
                    {formatTL(
                      displayedTableTickets.reduce((sum, t) => {
                        const { grandTotal } = calculateTicketTotal(t.parts, t.laborCost, t.vatRate, t.discount, t.isVatIncluded, t.roundingAdjustment || 0);
                        const periodKey = reportMode === 'daily' ? selectedDayKey : selectedMonthKey;
                        return sum + getTicketPaymentSplitForPeriod(t, periodKey).total;
                      }, 0)
                    )}
                  </td>
                  <td className="py-3 px-3 text-right font-mono text-amber-700">
                    {formatTL(
                      displayedTableTickets.reduce((sum, t) => {
                        const { grandTotal } = calculateTicketTotal(t.parts, t.laborCost, t.vatRate, t.discount, t.isVatIncluded, t.roundingAdjustment || 0);
                        return sum + Math.max(0, grandTotal - getTicketPaymentSplit(t).total);
                      }, 0)
                    )}
                  </td>
                  {!isLimitedTechnician && (
                    <>
                      <td className="py-3 px-3 text-right font-mono text-rose-700">
                        {formatTL(displayedTableTickets.reduce((sum, t) => sum + calculateTicketPartsCost(t.parts), 0))}
                      </td>
                      <td className="py-3 px-3 text-right font-mono text-purple-700">
                        {formatTL(
                          displayedTableTickets.reduce((sum, t) => {
                            const { vatAmount, grandTotal } = calculateTicketTotal(t.parts, t.laborCost, t.vatRate, t.discount, t.isVatIncluded, t.roundingAdjustment || 0);
                            const periodKey = reportMode === 'daily' ? selectedDayKey : selectedMonthKey;
                            const periodPaid = getTicketPaymentSplitForPeriod(t, periodKey).total;
                            const ratio = grandTotal > 0 ? periodPaid / grandTotal : 0;
                            return sum + vatAmount * ratio;
                          }, 0)
                        )}
                      </td>
                      <td className="py-3 px-3 text-right font-mono text-slate-900">
                        {formatTL(displayedTableTickets.reduce((sum, t) => {
                          const { grandTotal } = calculateTicketTotal(t.parts, t.laborCost, t.vatRate, t.discount, t.isVatIncluded, t.roundingAdjustment || 0);
                          return sum + grandTotal;
                        }, 0))}
                      </td>
                      <td className="py-3 px-3 text-right font-mono text-emerald-700">
                        <div>
                          {formatTL(displayedTableTickets.reduce((sum, t) => {
                            const { subtotal } = calculateTicketTotal(t.parts, t.laborCost, t.vatRate, t.discount, t.isVatIncluded, t.roundingAdjustment || 0);
                            const cost = calculateTicketPartsCost(t.parts);
                            const periodKey = reportMode === 'daily' ? selectedDayKey : selectedMonthKey;
                            const periodPaid = getTicketPaymentSplitForPeriod(t, periodKey).total;
                            const { grandTotal } = calculateTicketTotal(t.parts, t.laborCost, t.vatRate, t.discount, t.isVatIncluded, t.roundingAdjustment || 0);
                            const ratio = grandTotal > 0 ? periodPaid / grandTotal : 0;
                            return sum + (subtotal * ratio - cost);
                          }, 0))}
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </>
  )}
</div>
  );
};

function ArrowRightSmall() {
  return (
    <svg className="w-3 h-3 text-current" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}
